import React from 'react';
import PropTypes from 'prop-types';
import ErrorFallback from './ErrorFallback';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);

    if (typeof window !== 'undefined' && window.Sentry?.captureException) {
      window.Sentry.captureException(error, { extra: errorInfo });
    }
  }

  componentDidUpdate(prevProps) {
    const { resetKey } = this.props;
    if (resetKey !== undefined && resetKey !== prevProps.resetKey && this.state.hasError) {
      this.resetBoundary();
    }
  }

  resetBoundary = () => {
    this.setState({ hasError: false, error: null });
    const { onReset } = this.props;
    if (onReset) onReset();
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, variant, showDetails } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <ErrorFallback
          variant={variant}
          onReset={this.resetBoundary}
          showDetails={showDetails ?? process.env.NODE_ENV === 'development'}
          error={error}
        />
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  variant: PropTypes.oneOf(['global', 'page']),
  resetKey: PropTypes.string,
  onReset: PropTypes.func,
  showDetails: PropTypes.bool,
};

ErrorBoundary.defaultProps = {
  variant: 'page',
  fallback: null,
  resetKey: undefined,
  onReset: null,
  showDetails: undefined,
};

export default ErrorBoundary;
